import { useState, useEffect, useCallback } from 'react';
import type { CategorySlug } from '@features/category-navigation';
import type { SummaryResponse } from '../types';
import { cachedSummaryApi } from '../api/summary-api';

interface UseSummaryResult {
  summaryResponse: SummaryResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  // Sub-category state
  selectedSubCategory: string | null;
  setSelectedSubCategory: (slug: string | null) => void;
}

export function useSummary(
  category: CategorySlug,
  date?: string,
  edition?: string
): UseSummaryResult {
  const [summaryResponse, setSummaryResponse] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let data: SummaryResponse;
      if (date) {
        data = await cachedSummaryApi.getSummaryByDate(category, date, edition);
      } else {
        data = await cachedSummaryApi.getSummaryLatest(category);
      }

      setSummaryResponse(data);

      // Auto-select first sub-category if available
      if (data.hasSubCategories && data.subCategorySummaries?.length) {
        const sortedSubs = [...data.subCategorySummaries].sort(
          (a, b) => a.subCategory.sortOrder - b.subCategory.sortOrder
        );
        setSelectedSubCategory(sortedSubs[0].subCategory.slug);
      } else {
        setSelectedSubCategory(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch summary';
      setError(errorMessage);
      setSummaryResponse(null);
    } finally {
      setLoading(false);
    }
  }, [category, date, edition]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return {
    summaryResponse,
    loading,
    error,
    refetch: fetchSummary,
    selectedSubCategory,
    setSelectedSubCategory,
  };
}
